import { PresenterUnhandledResponseError, type Presenter } from "../presentation/presenter"
import { loginResponse, type LoginResponse } from "../presentation/responses/login"
import type { Session } from "../session/session"

export interface ApplicationAuthenticationOptions {
  readonly suppressPasswordManager?: boolean
}

export type ApplicationAuthenticationResult = "authenticated" | "cancelled" | "unavailable"

export interface ApplicationAuthentication {
  /** Shares one challenge. Aborting the last caller closes it. */
  request(signal?: AbortSignal): Promise<ApplicationAuthenticationResult>
}

interface Challenge {
  readonly controller: AbortController
  readonly promise: Promise<ApplicationAuthenticationResult>
  users: number
}

/** @internal Authentication presentation is application-owned, not document-owned. */
export function createApplicationAuthentication(
  session: Session,
  presenter: Presenter,
  options: ApplicationAuthenticationOptions | false = {}
): ApplicationAuthentication & { dispose(): void } {
  let challenge: Challenge | null = null
  let disposed = false

  function begin(): Challenge {
    const controller = new AbortController()
    let resolve!: (value: ApplicationAuthenticationResult) => void
    let reject!: (error: unknown) => void
    const promise = new Promise<ApplicationAuthenticationResult>((yes, no) => {
      resolve = yes
      reject = no
    })
    const current: Challenge = { controller, promise, users: 0 }
    challenge = current
    const baseUrl = session.accessContext.baseUrl
    let settled = false
    let unsubscribe: () => void = () => undefined
    let unsubscribeAccess: () => void = () => undefined
    const finish = (result: ApplicationAuthenticationResult, error?: unknown) => {
      if (settled) return
      settled = true
      unsubscribe()
      unsubscribeAccess()
      controller.signal.removeEventListener("abort", abort)
      if (challenge === current) challenge = null
      if (error === undefined) resolve(result)
      else reject(error)
      // Login changes the presentation access context. Other presentations must
      // retain their existing access-context protections.
      controller.abort()
    }
    const abort = () => finish("cancelled", controller.signal.reason)
    const reconcile = () => {
      if (session.accessContext.baseUrl !== baseUrl) finish("cancelled")
      else if (session.status === "signed-in") finish("authenticated")
    }
    unsubscribe = session.subscribe(reconcile)
    unsubscribeAccess = session.subscribeAccessContext(reconcile)
    controller.signal.addEventListener("abort", abort, { once: true })
    const response: LoginResponse = {
      username: session.user?.username,
      suppressPasswordManager: options === false ? undefined : options.suppressPasswordManager,
    }
    void presenter.present(loginResponse, response, controller.signal).then(
      (accepted) => finish(session.status === "signed-in" ? "authenticated" : accepted ? "unavailable" : "cancelled"),
      (error) => {
        // Session.login publishes the new identity before its dialog completes.
        if (session.status === "signed-in") finish("authenticated")
        else if (error instanceof PresenterUnhandledResponseError) finish("unavailable")
        else finish("unavailable", error)
      }
    )
    reconcile()
    return current
  }

  return {
    async request(signal) {
      signal?.throwIfAborted()
      await session.whenRestored()
      signal?.throwIfAborted()
      if (disposed) throw new Error("Application authentication has been disposed.")
      if (session.status === "signed-in") return "authenticated"
      if (options === false) return "unavailable"
      const current = challenge ?? begin()
      current.users += 1
      return new Promise<ApplicationAuthenticationResult>((resolve, reject) => {
        let released = false
        const release = () => {
          if (released) return
          released = true
          signal?.removeEventListener("abort", abort)
          current.users -= 1
          if (current.users === 0 && challenge === current) current.controller.abort()
        }
        const abort = () => {
          release()
          reject(signal!.reason)
        }
        signal?.addEventListener("abort", abort, { once: true })
        current.promise.then(
          (result) => {
            release()
            resolve(result)
          },
          (error) => {
            release()
            reject(error)
          }
        )
        if (signal?.aborted) abort()
      })
    },
    dispose() {
      disposed = true
      challenge?.controller.abort(new DOMException("Application disposed.", "AbortError"))
    },
  }
}
